import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft, Info, Calculator, RotateCcw } from 'lucide-react';
import Layout from '../../../../components/Layout';
import { Button } from '../../../../components/ui/Button';
import { BottomSheet } from '../../../../components/ui/BottomSheet';
import { LABEL_STANDARD } from '../../../../lib/formStyles';

/**
 * BS 7671 Table 43.1 k values.
 * Key: conductorTypeId_conductorMaterialId -> { k, initialTemp, finalTemp }
 * Temperatures in °C. k used in adiabatic equation: t = (k² × S²) / I²
 */
const K_VALUES = {
  'thermoplastic-90_copper': { k: 100, initialTemp: 90, finalTemp: 250 },
  'thermoplastic-90_aluminium': { k: 66, initialTemp: 90, finalTemp: 250 },
  'thermoplastic-90_tin-soldered': { k: 100, initialTemp: 90, finalTemp: 250 },
  'thermoplastic-70_copper': { k: 115, initialTemp: 70, finalTemp: 160 },
  'thermoplastic-70_aluminium': { k: 76, initialTemp: 70, finalTemp: 160 },
  'thermoplastic-70_tin-soldered': { k: 115, initialTemp: 70, finalTemp: 160 },
  'thermosetting-90_copper': { k: 143, initialTemp: 90, finalTemp: 250 },
  'thermosetting-90_aluminium': { k: 95, initialTemp: 90, finalTemp: 250 },
  'thermosetting-90_tin-soldered': { k: 143, initialTemp: 90, finalTemp: 250 },
  'thermosetting-60_copper': { k: 116, initialTemp: 60, finalTemp: 200 },
  'thermosetting-60_aluminium': { k: 77, initialTemp: 60, finalTemp: 200 },
  'thermosetting-60_tin-soldered': { k: 116, initialTemp: 60, finalTemp: 200 },
  'mi-thermoplastic-70_copper': { k: 115, initialTemp: 70, finalTemp: 160 },
  'mi-thermoplastic-70_aluminium': { k: 76, initialTemp: 70, finalTemp: 160 },
  'mi-thermoplastic-70_tin-soldered': { k: 115, initialTemp: 70, finalTemp: 160 },
  'mi-bare-105_copper': { k: 135, initialTemp: 105, finalTemp: 250 },
  'mi-bare-105_aluminium': { k: 90, initialTemp: 105, finalTemp: 250 },
  'mi-bare-105_tin-soldered': { k: 135, initialTemp: 105, finalTemp: 250 },
};

const CONDUCTOR_TYPES = [
  { id: 'thermoplastic-90', label: 'Thermoplastic 90°C' },
  { id: 'thermoplastic-70', label: 'Thermoplastic 70°C' },
  { id: 'thermosetting-90', label: 'Thermosetting 90°C' },
  { id: 'thermosetting-60', label: 'Thermosetting 60°C' },
  { id: 'mi-thermoplastic-70', label: 'Mineral Insulated Thermoplastic Sheath 70°C' },
  { id: 'mi-bare-105', label: 'Mineral Insulated Bare (No Sheath) 105°C' },
];

const CONDUCTOR_MATERIALS = [
  { id: 'copper', label: 'Copper' },
  { id: 'aluminium', label: 'Aluminium' },
  { id: 'tin-soldered', label: 'Tin soldered joints in copper conductors' },
];

const initialFormState = {
  conductorType: '',
  conductorMaterial: '',
  conductorSize: '',
  faultCurrent: '',
};

export default function CableLimitingTempCalc() {
  const location = useLocation();
  const isMobile = location.pathname.startsWith('/tenant/mobile');

  const [form, setForm] = useState(initialFormState);
  const [results, setResults] = useState(null);
  const [conductorTypeSheetOpen, setConductorTypeSheetOpen] = useState(false);
  const [conductorMaterialSheetOpen, setConductorMaterialSheetOpen] = useState(false);
  const [infoSheetOpen, setInfoSheetOpen] = useState(false);

  const backPath = isMobile ? '/tenant/mobile/calculators' : '/tenant/tools/calculators';

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const getKVals = () => {
    const key = `${form.conductorType}_${form.conductorMaterial}`;
    return K_VALUES[key] || null;
  };

  const handleCalculate = () => {
    const S = parseFloat(form.conductorSize);
    const I_kA = parseFloat(form.faultCurrent);
    const kData = getKVals();

    if (!form.conductorType || !form.conductorMaterial || !S || !I_kA || !kData) {
      setResults(null);
      return;
    }

    const I = I_kA * 1000; // kA to A
    const t = (Math.pow(kData.k, 2) * Math.pow(S, 2)) / Math.pow(I, 2);

    setResults({
      S,
      I: I_kA,
      k: kData.k,
      t: t.toFixed(4),
      initialTemp: kData.initialTemp,
      finalTemp: kData.finalTemp,
    });
  };

  const handleReset = () => {
    setForm(initialFormState);
    setResults(null);
  };

  const conductorTypeLabel = form.conductorType
    ? CONDUCTOR_TYPES.find((c) => c.id === form.conductorType)?.label || 'SELECT'
    : 'SELECT';
  const conductorMaterialLabel = form.conductorMaterial
    ? CONDUCTOR_MATERIALS.find((c) => c.id === form.conductorMaterial)?.label || 'SELECT'
    : 'SELECT';

  const content = (
    <div className="max-w-2xl mx-auto">
      {/* Header bar - green, matching screenshots */}
      <div className="flex items-center justify-between bg-green-600 dark:bg-green-700 px-4 py-3 rounded-t-lg -mx-4 -mt-4 mb-4 sm:rounded-t-none sm:mx-0 sm:mt-0 sm:-mx-0 sm:-mt-0 sm:rounded-lg">
        <Link
          to={backPath}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/20 transition-colors"
          aria-label="Back"
        >
          <ChevronLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-lg font-semibold text-white">Cable LT Time Calc</h1>
        <button
          type="button"
          onClick={() => setInfoSheetOpen(true)}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-green-500 dark:bg-green-600 text-white hover:bg-white/20 transition-colors"
          aria-label="Information"
        >
          <Info className="h-5 w-5" />
        </button>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-700 dark:text-gray-300 mb-6">
        Calculate the time (approximately), in which a given fault current will raise the live conductors
        from the highest permissible temperature in normal duty to the limiting temperature.
      </p>

      {/* Input fields */}
      <div className="space-y-4 mb-6">
        {/* Conductor Type - trigger for BottomSheet on mobile, native select on desktop */}
        <div>
          <label className={LABEL_STANDARD}>Conductor Type</label>
          <button
            type="button"
            onClick={() => setConductorTypeSheetOpen(true)}
            className="flex h-9 w-full items-center justify-between rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-left text-sm text-gray-900 dark:text-gray-100"
          >
            <span className={form.conductorType ? '' : 'text-gray-500 dark:text-gray-400'}>{conductorTypeLabel}</span>
            <svg className="h-4 w-4 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {/* Conductor Material */}
        <div>
          <label className={LABEL_STANDARD}>Conductor Material</label>
          <button
            type="button"
            onClick={() => setConductorMaterialSheetOpen(true)}
            className="flex h-9 w-full items-center justify-between rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-left text-sm text-gray-900 dark:text-gray-100"
          >
            <span className={form.conductorMaterial ? '' : 'text-gray-500 dark:text-gray-400'}>{conductorMaterialLabel}</span>
            <svg className="h-4 w-4 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {/* Conductor Size */}
        <div>
          <label className={LABEL_STANDARD}>Conductor Size</label>
          <div className="flex rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 overflow-hidden">
            <span className="inline-flex items-center px-3 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 border-r border-gray-300 dark:border-gray-600">
              mm²
            </span>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              step="0.5"
              placeholder="Enter cable size"
              value={form.conductorSize}
              onChange={(e) => handleChange('conductorSize', e.target.value)}
              className="flex-1 min-w-0 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 bg-transparent border-0 focus:outline-none focus:ring-0"
            />
          </div>
        </div>

        {/* Measured Fault Current */}
        <div>
          <label className={LABEL_STANDARD}>Measured Fault Current</label>
          <div className="flex rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 overflow-hidden">
            <span className="inline-flex items-center px-3 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 border-r border-gray-300 dark:border-gray-600">
              kA
            </span>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              step="0.1"
              placeholder="Enter measured fault current kA"
              value={form.faultCurrent}
              onChange={(e) => handleChange('faultCurrent', e.target.value)}
              className="flex-1 min-w-0 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 bg-transparent border-0 focus:outline-none focus:ring-0"
            />
          </div>
        </div>
      </div>

      {/* Results section */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
        <div className="bg-green-600 dark:bg-green-700 px-4 py-2">
          <h2 className="text-sm font-semibold text-white">Results</h2>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-2 pr-4 text-gray-700 dark:text-gray-300 font-medium">S</th>
                <th className="text-left py-2 pr-4 text-gray-700 dark:text-gray-300 font-medium">I</th>
                <th className="text-left py-2 pr-4 text-gray-700 dark:text-gray-300 font-medium">k</th>
                <th className="text-left py-2 text-gray-700 dark:text-gray-300 font-medium">t</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-100 dark:border-gray-700">
                <td className="py-2 pr-4 text-gray-900 dark:text-gray-100">{results?.S ?? '-S-'}</td>
                <td className="py-2 pr-4 text-gray-900 dark:text-gray-100">{results?.I ?? '-I-'}</td>
                <td className="py-2 pr-4 text-gray-900 dark:text-gray-100">{results?.k ?? '-k-'}</td>
                <td className="py-2 text-gray-900 dark:text-gray-100">{results?.t ?? '-t-'}</td>
              </tr>
              <tr>
                <td colSpan={4} className="py-2 text-gray-600 dark:text-gray-400 text-xs">
                  Initial Temperature → {results?.initialTemp ?? '-°C-'} °C
                </td>
              </tr>
              <tr>
                <td colSpan={4} className="py-1 pb-2 text-gray-600 dark:text-gray-400 text-xs">
                  Final Temperature → {results?.finalTemp ?? '-°C-'} °C
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Action buttons */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <Button
          onClick={handleCalculate}
          className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white"
        >
          <Calculator className="h-4 w-4" />
          Calculate
        </Button>
        <Button
          variant="destructive"
          onClick={handleReset}
          className="flex items-center justify-center gap-2"
        >
          <RotateCcw className="h-4 w-4" />
          Reset
        </Button>
      </div>

      {/* Key */}
      <div className="space-y-1 mb-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Key:</h3>
        <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
          <li>S : Conductor size mm²</li>
          <li>I : Effective fault current</li>
          <li>k : Conductor k value, see table 43.1</li>
          <li>t : Duration in seconds</li>
        </ul>
      </div>

      {/* Link and notes */}
      <div className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
        <a
          href="https://electrical.theiet.org/media/1617/appendix-4-of-bs-7671.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 dark:text-blue-400 hover:underline block"
        >
          Protection Against Exceeding Cable Limiting Temperature
        </a>
        <p>
          Initial &amp; Final Temperature and k values are from BS 7671 table 43.1 for common materials and fault
          current disconnection times up to 5 seconds. Refer to BS 7671 for specific details. BS 7454 - Method for
          calculation of thermally permissible short-circuit currents, taking into account non-adiabatic heating effects.
        </p>
      </div>

      {/* Bottom sheets for selection */}
      <BottomSheet
        open={conductorTypeSheetOpen}
        onOpenChange={setConductorTypeSheetOpen}
        title="Conductor Type"
      >
        <div className="space-y-1">
          {CONDUCTOR_TYPES.map((opt) => (
            <label
              key={opt.id}
              className="flex items-center gap-3 py-3 px-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
            >
              <input
                type="radio"
                name="conductorType"
                value={opt.id}
                checked={form.conductorType === opt.id}
                onChange={() => {
                  handleChange('conductorType', opt.id);
                  setConductorTypeSheetOpen(false);
                }}
                className="h-4 w-4 text-blue-600"
              />
              <span className="text-gray-900 dark:text-gray-100">{opt.label}</span>
            </label>
          ))}
        </div>
      </BottomSheet>

      <BottomSheet
        open={conductorMaterialSheetOpen}
        onOpenChange={setConductorMaterialSheetOpen}
        title="Conductor Material"
      >
        <div className="space-y-1">
          {CONDUCTOR_MATERIALS.map((opt) => (
            <label
              key={opt.id}
              className="flex items-center gap-3 py-3 px-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
            >
              <input
                type="radio"
                name="conductorMaterial"
                value={opt.id}
                checked={form.conductorMaterial === opt.id}
                onChange={() => {
                  handleChange('conductorMaterial', opt.id);
                  setConductorMaterialSheetOpen(false);
                }}
                className="h-4 w-4 text-blue-600"
              />
              <span className="text-gray-900 dark:text-gray-100">{opt.label}</span>
            </label>
          ))}
        </div>
      </BottomSheet>

      <BottomSheet open={infoSheetOpen} onOpenChange={setInfoSheetOpen} title="About Cable LT Time Calc">
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-3">
          <p>
            This calculator uses the adiabatic equation from BS 7671 to determine the approximate time for a given
            fault current to raise live conductors from their highest permissible operating temperature to the limiting
            temperature.
          </p>
          <p>Formula: t = (k² × S²) / I²</p>
          <p>
            k values are taken from BS 7671 Table 43.1. For other k values or non-adiabatic conditions, refer to
            BS 7454.
          </p>
        </div>
      </BottomSheet>
    </div>
  );

  if (isMobile) {
    return (
      <div className="px-4 py-6 pb-8 min-h-screen bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
        {content}
      </div>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 px-4 sm:px-6 lg:px-8 py-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Cable Limiting Temperature Calculator</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Calculate the time for fault current to raise conductor temperature to its limiting value
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-6">
          {content}
        </div>
      </div>
    </Layout>
  );
}
